import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, Calendar, Users, Search, Camera, ArrowRight, Star } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { trackWorkshopEvent } from '@/utils/workshopAnalytics';

interface WorkshopListing {
  id: string;
  slug: string;
  title: string;
  location_name: string;
  latitude: number;
  longitude: number;
  price_display: string | null;
  max_participants: number | null;
  workshop_type: string;
  external_url: string | null;
  workshop_dates: Array<{ start_date: string; end_date: string; notes: string | null }>;
  workshop_photos: Array<{ photo_url: string; alt_text: string | null; sort_order: number }>;
  workshop_leaders: { business_name: string; profile_photo_url: string | null } | null;
}

const TYPE_LABELS: Record<string, string> = {
  astrophotography: 'Astrophotography',
  deep_sky: 'Deep Sky',
  landscape: 'Landscape',
  nature: 'Nature',
  mixed: 'Mixed',
};

export default function Workshops() {
  const [listings, setListings] = useState<WorkshopListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  useEffect(() => {
    loadListings();
  }, []);

  const loadListings = async () => {
    try {
      const { data, error } = await supabase
        .from('workshop_listings')
        .select(`
          id, slug, title, location_name, latitude, longitude,
          price_display, max_participants, workshop_type, external_url,
          workshop_dates(start_date, end_date, notes),
          workshop_photos(photo_url, alt_text, sort_order),
          workshop_leaders(business_name, profile_photo_url)
        `)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setListings(data || []);
    } catch (err) {
      console.error('Error loading workshops:', err);
    } finally {
      setLoading(false);
    }
  };

  const getNextDate = (dates: WorkshopListing['workshop_dates']) => {
    const now = new Date().toISOString().split('T')[0];
    const future = dates
      .filter(d => d.end_date >= now)
      .sort((a, b) => a.start_date.localeCompare(b.start_date));
    return future[0] || dates.sort((a, b) => b.start_date.localeCompare(a.start_date))[0];
  };

  const filteredListings = useMemo(() => {
    const now = new Date().toISOString().split('T')[0];

    // Hide workshops where all dates are in the past
    let filtered = listings.filter(l =>
      !l.workshop_dates?.length || l.workshop_dates.some(d => d.end_date >= now)
    );

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(l =>
        l.title.toLowerCase().includes(q) ||
        l.location_name.toLowerCase().includes(q) ||
        l.workshop_leaders?.business_name.toLowerCase().includes(q)
      );
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(l => l.workshop_type === typeFilter);
    }

    // Sort
    filtered = [...filtered].sort((a, b) => {
      if (sortBy === 'date') {
        const dateA = getNextDate(a.workshop_dates)?.start_date || '9999';
        const dateB = getNextDate(b.workshop_dates)?.start_date || '9999';
        return dateA.localeCompare(dateB);
      }
      if (sortBy === 'price') {
        return (a.price_display || '').localeCompare(b.price_display || '');
      }
      return a.location_name.localeCompare(b.location_name);
    });

    return filtered;
  }, [listings, searchQuery, typeFilter, sortBy]);

  return (
    <>
      <SEOHead
        title="Astrophotography Workshops | Milky Way Planner"
        description="Find astrophotography workshops near dark sky locations. Browse by place, date, and type, and see upcoming sessions listed on Milky Way Planner."
        canonicalUrl="/workshops"
      />
      <div className="min-h-screen bg-background">
        {/* Hero */}
        <section className="bg-[#0f1923] text-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
            <p className="text-gold uppercase text-sm font-semibold tracking-[0.14em] mb-4">
              Workshop Directory
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight mb-4 leading-[1.1]">
              Astrophotography Workshops
            </h1>
            <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
              Learn from experienced photographers at the best dark sky locations worldwide.
            </p>

            {/* Search */}
            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by location, title, or instructor..."
                className="pl-12 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:bg-white/15 rounded-[4px]"
              />
            </div>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-8">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="astrophotography">Astrophotography</SelectItem>
                <SelectItem value="deep_sky">Deep Sky</SelectItem>
                <SelectItem value="landscape">Landscape</SelectItem>
                <SelectItem value="nature">Nature</SelectItem>
                <SelectItem value="mixed">Mixed</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Soonest</SelectItem>
                <SelectItem value="price">Price</SelectItem>
                <SelectItem value="location">Location</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center gap-4 ml-auto">
              <Link to="/workshops/instructors" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                View instructors →
              </Link>
              <span className="text-sm text-muted-foreground">
                {filteredListings.length} workshop{filteredListings.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {/* Listings Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <Card key={i} className="animate-pulse">
                  <div className="h-48 bg-muted rounded-t-lg" />
                  <CardContent className="h-32" />
                </Card>
              ))}
            </div>
          ) : filteredListings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredListings.map(listing => {
                const heroPhoto = listing.workshop_photos?.sort((a, b) => a.sort_order - b.sort_order)[0];
                const nextDate = getNextDate(listing.workshop_dates || []);

                return (
                  <Link
                    key={listing.id}
                    to={`/workshops/${listing.slug}`}
                    className="group"
                    onClick={() => trackWorkshopEvent('card_click', {
                      workshopId: listing.id,
                      source: 'directory',
                      allowDuplicate: true,
                    })}
                  >
                    <Card className="overflow-hidden h-full hover:shadow-lg transition-shadow rounded-[4px]">
                      {/* Hero Image */}
                      <div className="h-48 overflow-hidden bg-[#0f1923]">
                        {heroPhoto ? (
                          <img
                            src={heroPhoto.photo_url}
                            alt={heroPhoto.alt_text || listing.title}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-slate-100">
                            <Camera className="w-10 h-10 text-slate-300" />
                          </div>
                        )}
                      </div>

                      <CardContent className="p-5 space-y-3">
                        {/* Type badge */}
                        <Badge variant="outline" className="text-xs">
                          {TYPE_LABELS[listing.workshop_type] || listing.workshop_type}
                        </Badge>

                        {/* Title */}
                        <h3 className="font-serif font-bold text-foreground text-lg leading-tight group-hover:text-[#c9922a] transition-colors">
                          {listing.title}
                        </h3>

                        {/* Leader */}
                        {listing.workshop_leaders && (
                          <div className="flex items-center gap-2">
                            {listing.workshop_leaders.profile_photo_url ? (
                              <img
                                src={listing.workshop_leaders.profile_photo_url}
                                alt=""
                                className="w-6 h-6 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-muted" />
                            )}
                            <span className="text-sm text-muted-foreground">{listing.workshop_leaders.business_name}</span>
                          </div>
                        )}

                        {/* Location + Date + Price */}
                        <div className="space-y-1.5 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{listing.location_name}</span>
                          </div>
                          {nextDate && (
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                              <span>
                                {format(parseISO(nextDate.start_date), 'MMM d')} – {format(parseISO(nextDate.end_date), 'MMM d, yyyy')}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Price + Participants */}
                        <div className="flex items-center justify-between pt-2 border-t">
                          {listing.price_display && (
                            <span className="font-semibold text-foreground">{listing.price_display}</span>
                          )}
                          {listing.max_participants && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {listing.max_participants} max
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-20">
              <Camera className="w-12 h-12 text-muted-foreground/20 mx-auto mb-6" />
              <h2 className="font-serif text-2xl font-bold text-foreground mb-3">
                {searchQuery || typeFilter !== 'all' ? 'No workshops match your search' : 'Workshops coming soon'}
              </h2>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto leading-relaxed">
                {searchQuery || typeFilter !== 'all'
                  ? 'Try adjusting your search or filters.'
                  : 'We\'re building our directory of astrophotography workshops. Check back soon, or list your own.'}
              </p>
              {searchQuery || typeFilter !== 'all' ? (
                <Button variant="outline" className="rounded-[4px]" onClick={() => { setSearchQuery(''); setTypeFilter('all'); }}>
                  Clear Filters
                </Button>
              ) : (
                <Link to="/workshops/list-your-workshop">
                  <Button variant="premium" className="gap-2">
                    List Your Workshop <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              )}
            </div>
          )}

          {/* CTA for workshop leaders */}
          {!loading && (
            <div className="mt-16 border-t border-border pt-16 text-center">
              <p className="text-gold-foreground uppercase text-sm font-semibold tracking-[0.14em] mb-4">
                For Instructors
              </p>
              <h2 className="font-serif text-2xl font-bold text-foreground mb-3 tracking-tight">
                Are you a workshop leader?
              </h2>
              <p className="text-muted-foreground mb-8 max-w-lg mx-auto leading-relaxed">
                List your astrophotography workshops on Milky Way Planner and reach thousands of photographers planning their next shoot.
              </p>
              <Link to="/workshops/list-your-workshop">
                <Button variant="outline" size="lg" className="gap-2 rounded-[4px]">
                  List Your Workshop
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
